<template>
    <!-- Desktop & tablet view: tablets get the desktop components per the
         ITDEVELOP-3147 breakpoint ruling — only phones (<768) fork below. -->
    <div v-if="overlaySize === 'desktop'" class="gap-sm flex items-center">
        <ButtonModal :title="title" icon-class="i-filter" placement="bottom-left">
            <section name="filter" class="gap-md flex w-96 flex-col">
                <h2>{{ title }}</h2>
                <div v-if="isLoading" class="flex justify-center p-4">
                    <i class="i-loading animate-spin text-2xl text-(--icon-primary)" />
                </div>
                <div v-else class="gap-sm flex flex-col">
                    <slot name="header-actions" :model="internalModel" :update="handleFilterUpdate"></slot>

                    <template v-for="filter in filters" :key="filter.key">
                        <InputSelect
                            v-if="!filter.hidden"
                            :label="filter.label"
                            :value="optionsAt(internalModel, filter.key)"
                            :options="filter.options"
                            :placeholder="filter.placeholder || 'Select'"
                            :multiselect="true"
                            :disabled="disabled"
                            @update="handleFilterUpdate(filter.key, $event)"
                        />
                    </template>

                    <slot
                        name="additional-filters"
                        :model="internalModel"
                        :update="handleFilterUpdate"
                        :apply="applyFilters"
                    ></slot>
                </div>

                <div class="mt-md flex items-center justify-between">
                    <slot name="footer-left" :model="internalModel" :update="handleFilterUpdate"></slot>
                    <div class="gap-sm ml-auto flex">
                        <Button @click="applyFilters" @keydown.enter="applyFilters">
                            <span>Apply</span>
                            <i class="i-checkmark text-inherit" />
                        </Button>
                        <button class="btn-link" @click="resetFilters" @keydown.enter="resetFilters">Reset</button>
                    </div>
                </div>
            </section>
        </ButtonModal>

        <!-- Desktop Active Filters Chips -->
        <div
            v-for="filter in activeFilters.slice(0, effectiveActiveFilterLimit)"
            :key="filter.value + filter.categoryName"
            data-testid="active-filter-chip"
            class="flex min-w-0 cursor-pointer items-center rounded-sm border-2 border-(--tag-primary) bg-(--tag-primary) capitalize hover:border-(--state-info-border) hover:bg-(--surface-highlight)"
            :class="isCompactDesktop ? 'max-w-36 gap-1 px-2 py-1.5' : 'pl-sm gap-sm p-2'"
            :title="`${filter.categoryName}: ${filter.label}`"
        >
            <span
                data-testid="active-filter-text"
                class="text-primary min-w-0 truncate text-[13px] leading-5.25 font-normal"
            >
                {{ filter.categoryName }}: {{ filter.label }}
            </span>
            <i
                data-testid="active-filter-remove"
                class="i-cancel-x ml-1 size-4 shrink-0 bg-center bg-no-repeat"
                style="mask-size: 12px 12px"
                @click="removeSingleFilter(filter)"
            />
        </div>
        <div
            v-if="activeFilters.length > effectiveActiveFilterLimit"
            data-testid="active-filter-overflow"
            class="inline-flex shrink-0 items-center rounded-sm border-2 border-(--tag-primary) bg-(--tag-primary) font-medium capitalize"
            :class="isCompactDesktop ? 'px-2 py-1 text-sm' : 'gap-xs p-2'"
        >
            +{{ activeFilters.length - effectiveActiveFilterLimit }}
        </div>
    </div>

    <!-- Mobile View -->
    <template v-else>
        <Button
            variant="tertiary"
            data-testid="filter-mobile-trigger"
            class="gap-xs flex items-center"
            @click="toggleMobileFilterSheet"
        >
            <i class="i-filter block size-6" />
            <span v-if="activeFilters.length > 0" class="text-(--state-info-fg)">
                {{ activeFilters.length }}
            </span>
        </Button>

        <!-- Full screen per the overlay rule (multi-facet filter, staged
             Apply). ui/sheet supplies what the old slot-overlay lacked: a
             portal, a scrim and a focus trap. Full screen covers the scrim, so
             the header keeps an explicit Cancel; hide-close per the
             duplicate-affordance rule. Selection is STAGED: the working copy
             reseeds from the committed model on every open, so Cancel, Escape
             and the scrim all genuinely discard. -->
        <Sheet v-model:open="showMobileFilter">
            <SheetContent side="bottom" class="flex h-dvh flex-col gap-0 p-0" hide-close>
                <!-- Title truly centered (relative + absolute X): a leading
                     Cancel made the grow-centered title sit right of optical
                     center. The X is the close affordance the old slot-overlay
                     header used; discard semantics are identical since the
                     sheet stages. Headers that carry actions (the select's
                     Cancel/Apply pair) keep the three-part row instead. -->
                <div class="p-sm relative flex items-center justify-center border-b border-(--border-subtle)">
                    <SheetTitle class="p-0 text-center text-lg font-bold">{{ title }}</SheetTitle>
                    <button
                        type="button"
                        aria-label="Close"
                        class="right-sm absolute"
                        @click="cancelMobileFilter"
                    >
                        <i class="i-exit block size-6" />
                    </button>
                </div>

                <div class="gap-md px-sm pt-sm flex flex-1 flex-col overflow-y-auto">
                    <slot name="mobile-header" :model="internalModel" :update="handleFilterUpdate"></slot>

                    <div v-if="isLoading" class="flex justify-center p-4">
                        <i class="i-loading animate-spin text-2xl text-(--icon-primary)" />
                    </div>

                    <template v-else>
                        <template v-for="filter in filters" :key="filter.key">
                            <div v-if="!filter.hidden && filter.options.length > 0" class="gap-xs flex flex-col">
                                <p class="text-lg font-bold">{{ filter.label }}</p>
                                <div class="mt-xs gap-sm flex flex-col">
                                    <div
                                        v-for="opt in filter.options"
                                        :key="opt.value"
                                        class="gap-sm flex items-center"
                                    >
                                        <input
                                            :id="`${filter.key}-${opt.value}`"
                                            type="checkbox"
                                            :value="opt"
                                            :checked="isChecked(filter.key, opt)"
                                            class="h-5 w-5"
                                            @change="toggleMobileFilter(filter.key, opt)"
                                        />
                                        <label :for="`${filter.key}-${opt.value}`">{{ opt.label }}</label>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </template>
                </div>

                <div
                    class="gap-xs px-sm py-sm bg-lightest shadow-up flex w-full items-center justify-between border-t border-(--border-strong)"
                >
                    <div class="gap-2xs xs:gap-0 flex">
                        <button
                            type="button"
                            class="-m-xs xs:m-0"
                            @click="resetFiltersMobile"
                        >
                            Reset
                        </button>
                        <slot
                            name="mobile-footer-left"
                            :model="internalModel"
                            :update="handleFilterUpdate"
                            :apply="applyFiltersMobile"
                        ></slot>
                    </div>
                    <Button type="button" class="btn" @click="applyFiltersMobile">
                        <span>Apply</span>
                        <i class="i-checkmark text-inherit" />
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    </template>
</template>

<script setup lang="ts" generic="TModel extends object">
import { Button } from '@/components/ui/button';
import ButtonModal from '@/components/misc/button-modal.vue';
import InputSelect from '@/components/support/input-select.vue';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import type { Option } from '@/stores/store-interfaces';
import { computed, ref, watch, type Ref } from 'vue';
import { useOverlaySize } from '@/composables/isDesktop';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

export interface FilterConfig {
    key: string;
    label: string;
    placeholder?: string;
    options: Option[];
    hidden?: boolean;
}

// A chip supplied by the parent for state it owns outside the configured option lists (legacy toggle, free text).
export interface ExtraActiveFilter {
    label: string;
    categoryName: string;
    value: string | number;
}

// `key` is set for chips derived from a configured filter; extra chips have none and are removed by the parent.
type ActiveFilter = ExtraActiveFilter & { key?: string };

const props = withDefaults(
    defineProps<{
        title: string;
        filters: FilterConfig[];
        modelValue: TModel;
        disabled?: boolean;
        activeFilterLimit?: number;
        isLoading?: boolean;
        extraActiveFilters?: ExtraActiveFilter[]; // For legacy tickets etc.
    }>(),
    {
        activeFilterLimit: 3,
        disabled: false,
        isLoading: false,
        extraActiveFilters: () => [],
    },
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: TModel): void;
    (e: 'apply', value: TModel): void;
    (e: 'reset'): void;
    (e: 'remove-extra', filter: ExtraActiveFilter): void;
}>();

const overlaySize = useOverlaySize();
const showMobileFilter = ref(false);

// Staging: the sheet edits a working copy seeded from the committed model on
// every open, so any close path other than Apply discards. The old
// slot-overlay kept dirty state across close/reopen.
watch(showMobileFilter, (open) => {
    if (open) internalModel.value = JSON.parse(JSON.stringify(props.modelValue));
});

const breakpoints = useBreakpoints(breakpointsTailwind);
const isXXL = breakpoints.greaterOrEqual('2xl');
const isXl = breakpoints.greaterOrEqual('xl');
const isCompactDesktop = computed(() => overlaySize.value === 'desktop' && !isXl.value);

const effectiveActiveFilterLimit = computed(() => {
    if (isXXL.value) return props.activeFilterLimit;
    if (isXl.value) return Math.min(props.activeFilterLimit, 2);
    return 1;
});

// Local state for tracking changes inside the modal
const internalModel = ref(JSON.parse(JSON.stringify(props.modelValue))) as Ref<TModel>;

// The configured filter keys hold Option[]; anything else in the model (free-text fields owned by the
// slots) is not an option list and reads as empty here.
function optionsAt(model: TModel, key: string): Option[] {
    const value = (model as Record<string, unknown>)[key];
    return Array.isArray(value) ? value : [];
}

// Watch for external changes to prop modelValue and sync internal state
watch(
    () => props.modelValue,
    (newVal) => {
        internalModel.value = JSON.parse(JSON.stringify(newVal));
    },
    { deep: true },
);

const activeFilters = computed(() => {
    const filters: ActiveFilter[] = [];

    // Use props.modelValue for displaying active chips (committed changes only)
    props.filters.forEach((filter) => {
        const selectedOptions = optionsAt(props.modelValue, filter.key);
        if (selectedOptions.length > 0) {
            filters.push(
                ...selectedOptions.map((item: Option) => ({
                    label: item.label,
                    value: item.value,
                    categoryName: filter.label, // Use label for display
                    key: filter.key, // Store key for removal
                })),
            );
        }
    });

    if (props.extraActiveFilters) {
        filters.push(...props.extraActiveFilters);
    }

    return filters;
});

function handleFilterUpdate(key: string, value: unknown) {
    // Only update internal state
    internalModel.value = { ...internalModel.value, [key]: value } as TModel;
}

function applyFilters() {
    emit('update:modelValue', internalModel.value);
    emit('apply', internalModel.value);
}

function toggleMobileFilterSheet() {
    showMobileFilter.value = !showMobileFilter.value;
}

function resetFiltersMobile() {
    resetFilters();
    showMobileFilter.value = false;
}

function cancelMobileFilter() {
    // The working copy reseeds on every open, so closing without Apply discards.
    showMobileFilter.value = false;
}

function applyFiltersMobile() {
    emit('update:modelValue', internalModel.value);
    emit('apply', internalModel.value);
    showMobileFilter.value = false;
}

function resetFilters() {
    // Reset internal state
    const resetState: Record<string, Option[]> = {};
    props.filters.forEach((f) => {
        resetState[f.key] = [];
    });

    internalModel.value = resetState as TModel;
    emit('reset'); // Parent handles logic, usually resets modelValue?
    // If parent handles reset by clearing props.modelValue, our watcher will pick it up and clear internalModel.
}

function removeSingleFilter(filter: ActiveFilter) {
    if (filter.key) {
        // Standard filter
        const currentOptions = optionsAt(props.modelValue, filter.key);
        const newOptions = currentOptions.filter((item: Option) => item.value !== filter.value);

        const newValue = { ...props.modelValue, [filter.key]: newOptions } as TModel;
        emit('update:modelValue', newValue);
        emit('apply', newValue); // Auto-apply on removal
    } else {
        emit('remove-extra', filter);
    }
}

// Mobile specific helpers
function isChecked(key: string, option: Option) {
    // Use internalModel for checkbox state
    return optionsAt(internalModel.value, key).some((o: Option) => o.value === option.value);
}

function toggleMobileFilter(key: string, option: Option) {
    const currentOptions = optionsAt(internalModel.value, key);
    const index = currentOptions.findIndex((o: Option) => o.value === option.value);
    let newOptions;
    if (index === -1) {
        newOptions = [...currentOptions, option];
    } else {
        newOptions = currentOptions.filter((o: Option) => o.value !== option.value);
    }
    handleFilterUpdate(key, newOptions);
}
</script>
